// Mirrors the refusal code returned by bonni-backend's before-user-created hook
// for an identity that authenticated through LinkedIn Okta but is outside the
// study cohort. Kept separate from the invite-only path so the two refusals can
// say different things.
export const LINKEDIN_COHORT_ERROR_CODE = "LinkedIn_SSO_access_is_limited_to_the_study_group.";

export const LINKEDIN_COHORT_ROUTE_CODE = "linkedin-not-in-cohort";

export function hasLinkedInCohortAuthError(search: string, hash: string): boolean {
  const searchParams = new URLSearchParams(search);
  const hashParams = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);

  const parts = [
    searchParams.get("error"),
    searchParams.get("error_code"),
    searchParams.get("error_description"),
    hashParams.get("error"),
    hashParams.get("error_code"),
    hashParams.get("error_description"),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return parts.includes(LINKEDIN_COHORT_ERROR_CODE.toLowerCase());
}

// Mirrors the refusal code returned by bonni-backend's custom-access-token
// hook for a LinkedIn cohort participant whose study access has since been
// revoked. Distinct from LINKEDIN_COHORT_ERROR_CODE above: that one means
// "never was in the study group", this one means "was, isn't anymore" — the
// two say different things and must not be mistaken for each other.
export const LINKEDIN_REVOKED_ERROR_CODE = "LinkedIn_SSO_access_to_the_study_has_ended.";

export const LINKEDIN_REVOKED_ROUTE_CODE = "linkedin-access-ended";

export function hasLinkedInRevokedAuthError(search: string, hash: string): boolean {
  const searchParams = new URLSearchParams(search);
  const hashParams = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);

  const parts = [
    searchParams.get("error"),
    searchParams.get("error_code"),
    searchParams.get("error_description"),
    hashParams.get("error"),
    hashParams.get("error_code"),
    hashParams.get("error_description"),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return parts.includes(LINKEDIN_REVOKED_ERROR_CODE.toLowerCase());
}

// Marks that this browser started a LinkedIn cohort sign-in, so the /userinfo
// verification in PostAuth runs only for those.
//
// Google sign-ins also return a `provider_token`, so keying the verification
// off its presence alone POSTed every Google access token to the backend and
// would sign out any Google user who also had a linked LinkedIn identity.
const LINKEDIN_SIGN_IN_MARKER = "bonni.linkedin-sso.pending";

export function markLinkedInSignInStarted(): void {
  try {
    window.sessionStorage.setItem(LINKEDIN_SIGN_IN_MARKER, "1");
  } catch {
    // Private mode or blocked storage: verification is skipped and getMe
    // refuses to provision, which fails closed rather than unverified.
  }
}

export function consumeLinkedInSignInStarted(): boolean {
  try {
    const started = window.sessionStorage.getItem(LINKEDIN_SIGN_IN_MARKER) === "1";
    window.sessionStorage.removeItem(LINKEDIN_SIGN_IN_MARKER);
    return started;
  } catch {
    return false;
  }
}

// Refusal reasons that genuinely mean "this person is not in the study".
// Everything else the backend marks `applicable` — userinfo_failed,
// discovery_failed, stamp_failed, identity_absent — is a transient or
// infrastructural fault, and telling a real participant they aren't in the
// study because Okta hiccuped is worse than asking them to try again.
const NOT_IN_STUDY_REASONS = new Set(["sub_mismatch", "email_unverified"]);

export function isNotInStudyReason(reason: unknown): boolean {
  return typeof reason === "string" && NOT_IN_STUDY_REASONS.has(reason);
}

// TEMPORARY, for the LinkedIn pilot (2026-10-05). LinkedIn's Okta sends no
// email_verified, so Supabase answers an admitted participant's FIRST sign-in
// with provider_email_needs_verification. bonni-backend's before-user-created
// hook confirms that account seconds later (linkedin-cohort-confirm.ts), so
// PostAuth waits and retries the sign-in once. Remove with the backend bridge.
const PROVIDER_EMAIL_NEEDS_VERIFICATION = "provider_email_needs_verification";

export const LINKEDIN_CONFIRMATION_ROUTE_CODE = "linkedin-sign-in-incomplete";

/** Long enough for the backend to see the account Supabase just saved. */
export const LINKEDIN_CONFIRMATION_RETRY_DELAY_MS = 4_000;

export function hasProviderEmailNeedsVerificationError(search: string, hash: string): boolean {
  const searchParams = new URLSearchParams(search);
  const hashParams = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);

  return [searchParams.get("error_code"), hashParams.get("error_code")].includes(
    PROVIDER_EMAIL_NEEDS_VERIFICATION
  );
}

const LINKEDIN_CONFIRMATION_RETRY_MARKER = "bonni.linkedin-sso.confirmation-retried";

/**
 * True the first time it is called in a sign-in attempt, false after: the
 * retry happens at most once, so a confirmation that never lands ends on an
 * explanation instead of a redirect loop. Fails closed (no retry) when storage
 * is blocked, since the marker could not stop a loop.
 */
export function claimLinkedInConfirmationRetry(): boolean {
  try {
    if (window.sessionStorage.getItem(LINKEDIN_CONFIRMATION_RETRY_MARKER) === "1") return false;
    window.sessionStorage.setItem(LINKEDIN_CONFIRMATION_RETRY_MARKER, "1");
    return true;
  } catch {
    return false;
  }
}

/** Resets the single retry: on a fresh sign-in, and once a sign-in succeeds. */
export function clearLinkedInConfirmationRetry(): void {
  try {
    window.sessionStorage.removeItem(LINKEDIN_CONFIRMATION_RETRY_MARKER);
  } catch {
    // Blocked storage: claim already refuses to retry, so there is nothing to reset.
  }
}

// Mirrors the refusal bonni-backend's before-user-created hook returns when a
// linkedin.com account is created any way other than LinkedIn sign-in (Google,
// password, a Router invite). Those addresses sign in with "Continue with
// LinkedIn" only.
export const LINKEDIN_SIGN_IN_REQUIRED_ERROR_CODE = "LinkedIn_accounts_sign_in_with_LinkedIn.";

export const LINKEDIN_SIGN_IN_REQUIRED_ROUTE_CODE = "linkedin-sign-in-required";

export function hasLinkedInSignInRequiredAuthError(search: string, hash: string): boolean {
  const searchParams = new URLSearchParams(search);
  const hashParams = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);

  const parts = [
    searchParams.get("error_code"),
    searchParams.get("error_description"),
    hashParams.get("error_code"),
    hashParams.get("error_description"),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return parts.includes(LINKEDIN_SIGN_IN_REQUIRED_ERROR_CODE.toLowerCase());
}
